import { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import useFilePermissions from "~/hooks/useFilePermissions";
import { ProjectListItem, projectListPath } from "~/services/projects";
import { fetchAllPages } from "~/utils/fetchAllPages";

interface ProjectFilesContextValue {
  /** Id in `research/projects/<projectId>` that the file browser's project directory maps to. */
  projectId: string;
  /** Router path the file browser's paths are relative to. */
  basePath: string;
}

/**
 * Points the file browser at a project's files directory outside the workbench
 * route subtree, which otherwise supplies both values from its `:projectId` route.
 */
const ProjectFilesContext = createContext<ProjectFilesContextValue | null>(null);

export const ProjectFilesProvider = ProjectFilesContext.Provider;

export const useProjectFilesContext = () => useContext(ProjectFilesContext);

/** The project id the file browser resolves `research/projects/<id>` with, if any. */
export const useFilesProjectId = (): string | undefined => {
  const context = useProjectFilesContext();
  const { projectId } = useParams();
  return context?.projectId ?? projectId;
};

/** S3 key of a nextgen project's files directory. */
export const projectFilesKey = (projectId: string) => `research/projects/${projectId}`;

/** The backend caps a single projects page at 100 (its `MAX_LIMIT`). */
const PROJECT_PAGE_SIZE = 100;
const NO_PROJECTS: ProjectListItem[] = [];

/**
 * The caller's usable projects that have a files directory they hold a grant on.
 * Undefined while loading; empty outside a project's files, or when either
 * request fails, since a failed permissions read means no access.
 */
export const useProjectFilesDirectories = (): ProjectListItem[] | undefined => {
  const isProjectFiles = !!useProjectFilesContext();
  // Its own key: other callers cache a single page under `projectListPath()`.
  const { data: projects, error: projectsError } = useSWR(
    isProjectFiles ? [projectListPath(), "all-pages"] : null,
    ([url]: [string, string]) => fetchAllPages<ProjectListItem>(url, PROJECT_PAGE_SIZE)
  );
  const { data: permissions, error: permissionsError } = useFilePermissions();
  const directories = useMemo(
    () =>
      projects &&
      permissions &&
      projects.items.filter(({ id }) => !!permissions[`${projectFilesKey(id)}/*`]?.length),
    [projects, permissions]
  );
  if (!isProjectFiles || (!directories && (projectsError || permissionsError))) {
    return NO_PROJECTS;
  }
  return directories;
};
